import type { SVGProps } from 'react';

// Inline SVG icons (paths from the design artboards).
type P = SVGProps<SVGSVGElement> & { size?: number };

const base = (size: number, sw = 1.8): SVGProps<SVGSVGElement> => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: sw,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
});

export const IconFleet = ({ size = 20, ...p }: P) => (
  <svg {...base(size)} {...p}>
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </svg>
);
export const IconMap = ({ size = 20, ...p }: P) => (
  <svg {...base(size)} {...p}>
    <path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2z" />
    <path d="M9 4v14" />
    <path d="M15 6v14" />
  </svg>
);
export const IconMedia = ({ size = 20, ...p }: P) => (
  <svg {...base(size)} {...p}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m10 9 5 3-5 3z" />
  </svg>
);
export const IconDrive = ({ size = 20, ...p }: P) => (
  <svg {...base(size)} {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 3v3" />
    <path d="M12 18v3" />
    <path d="M3 12h3" />
    <path d="M18 12h3" />
  </svg>
);
export const IconAgent = ({ size = 20, ...p }: P) => (
  <svg {...base(size)} {...p}>
    <path d="M4 5h16v11H9l-5 4V5z" />
    <path d="M8 10h8" />
  </svg>
);
export const IconCalendar = ({ size = 20, ...p }: P) => (
  <svg {...base(size)} {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18" />
    <path d="M8 3v4" />
    <path d="M16 3v4" />
  </svg>
);
export const IconAutonomy = ({ size = 20, ...p }: P) => (
  <svg {...base(size)} {...p}>
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
    <path d="M19 16v5" />
    <path d="M16.5 18.5h5" />
  </svg>
);
export const IconBolt = ({ size = 16, ...p }: P) => (
  <svg {...base(size, 2)} {...p}>
    <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
  </svg>
);
export const IconMic = ({ size = 16, ...p }: P) => (
  <svg {...base(size, 2)} {...p}>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0" />
    <path d="M12 18v3" />
  </svg>
);
export const IconSpeaker = ({ size = 16, ...p }: P) => (
  <svg {...base(size, 2)} {...p}>
    <path d="M11 5 6 9H3v6h3l5 4V5z" />
    <path d="M15.5 8.5a5 5 0 0 1 0 7" />
    <path d="M18.5 5.5a9 9 0 0 1 0 13" />
  </svg>
);
export const IconMuted = ({ size = 16, ...p }: P) => (
  <svg {...base(size, 2)} {...p}>
    <path d="M11 5 6 9H3v6h3l5 4V5z" />
    <path d="m16 9 5 6" />
    <path d="m21 9-5 6" />
  </svg>
);
export const IconWave = ({ size = 16, ...p }: P) => (
  <svg {...base(size, 2)} {...p}>
    <path d="M12 3v18" />
    <path d="M8 7v10" />
    <path d="M16 7v10" />
    <path d="M4 10v4" />
    <path d="M20 10v4" />
  </svg>
);
export const IconPlay = ({ size = 12, ...p }: P) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden {...p}>
    <path d="M7 5v14l12-7z" />
  </svg>
);
export const IconPause = ({ size = 12, ...p }: P) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden {...p}>
    <path d="M7 5h4v14H7zM13 5h4v14h-4z" />
  </svg>
);
export const IconFit = ({ size = 18, ...p }: P) => (
  <svg {...base(size)} {...p}>
    <path d="M4 9V4h5" />
    <path d="M20 9V4h-5" />
    <path d="M4 15v5h5" />
    <path d="M20 15v5h-5" />
  </svg>
);
export const IconFollow = ({ size = 18, ...p }: P) => (
  <svg {...base(size)} {...p}>
    <circle cx="12" cy="12" r="7" />
    <circle cx="12" cy="12" r="2" />
    <path d="M12 2v3" />
    <path d="M12 19v3" />
    <path d="M2 12h3" />
    <path d="M19 12h3" />
  </svg>
);
export const IconSearch = ({ size = 16, ...p }: P) => (
  <svg {...base(size, 2)} stroke="#9BA1A8" {...p}>
    <circle cx="11" cy="11" r="6" />
    <path d="m20 20-4.5-4.5" />
  </svg>
);
export const IconGrip = ({ size = 14, ...p }: P) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#5C636B" aria-hidden {...p}>
    <circle cx="9" cy="6" r="1.6" />
    <circle cx="15" cy="6" r="1.6" />
    <circle cx="9" cy="12" r="1.6" />
    <circle cx="15" cy="12" r="1.6" />
    <circle cx="9" cy="18" r="1.6" />
    <circle cx="15" cy="18" r="1.6" />
  </svg>
);
export const IconLogout = ({ size = 18, ...p }: P) => (
  <svg {...base(size)} {...p}>
    <path d="M15 4h4v16h-4" />
    <path d="M10 8l-4 4 4 4" />
    <path d="M6 12h10" />
  </svg>
);
export const IconCamera = ({ size = 16, ...p }: P) => (
  <svg {...base(size, 2)} {...p}>
    <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
    <circle cx="12" cy="13" r="3.5" />
  </svg>
);
export const IconBack = ({ size = 18, ...p }: P) => (
  <svg {...base(size, 2)} {...p}>
    <path d="m15 5-7 7 7 7" />
  </svg>
);
export const IconCheck = ({ size = 12, ...p }: P) => (
  <svg {...base(size, 3)} {...p}>
    <path d="m5 12 5 5 9-10" />
  </svg>
);
export const IconUpload = ({ size = 16, ...p }: P) => (
  <svg {...base(size, 2)} {...p}>
    <path d="M12 16V4" />
    <path d="m7 9 5-5 5 5" />
    <path d="M4 20h16" />
  </svg>
);
export const IconTrash = ({ size = 14, ...p }: P) => (
  <svg {...base(size, 2)} {...p}>
    <path d="M4 7h16" />
    <path d="M9 7V4h6v3" />
    <path d="M6 7l1 13h10l1-13" />
  </svg>
);
// §6.2 scan editor (pencil / eraser) — paths from the "Map: edit real walls" artboard
export const IconHand = ({ size = 20, ...p }: P) => (
  <svg {...base(size)} {...p}>
    <path d="M18 11V6a2 2 0 0 0-4 0" />
    <path d="M14 10V4a2 2 0 0 0-4 0v2" />
    <path d="M10 10.5V6a2 2 0 0 0-4 0v8" />
    <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
  </svg>
);
export const IconPencil = ({ size = 20, ...p }: P) => (
  <svg {...base(size)} {...p}>
    <path d="M17 3a2.8 2.8 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5z" />
    <path d="m15 5 4 4" />
  </svg>
);
export const IconEraser = ({ size = 20, ...p }: P) => (
  <svg {...base(size)} {...p}>
    <path d="m7 21-4.3-4.3a1 1 0 0 1 0-1.4l10-10a1 1 0 0 1 1.4 0l5.6 5.6a1 1 0 0 1 0 1.4L11 21" />
    <path d="M22 21H7" />
    <path d="m5 11 9 9" />
  </svg>
);
export const IconUndo = ({ size = 18, ...p }: P) => (
  <svg {...base(size)} {...p}>
    <path d="M9 14 4 9l5-5" />
    <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
  </svg>
);
export const IconRedo = ({ size = 18, ...p }: P) => (
  <svg {...base(size)} {...p}>
    <path d="m15 14 5-5-5-5" />
    <path d="M20 9H9.5a5.5 5.5 0 0 0 0 11H13" />
  </svg>
);
export const IconEye = ({ size = 18, ...p }: P) => (
  <svg {...base(size)} {...p}>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
export const IconWarn = ({ size = 18, ...p }: P) => (
  <svg {...base(size)} {...p}>
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </svg>
);
export const IconAlert = ({ size = 18, ...p }: P) => (
  <svg {...base(size)} {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5" />
    <path d="M12 16h.01" />
  </svg>
);
// §6.3 blocker cards: a wall in the way (virtual or scan)
export const IconWall = ({ size = 16, ...p }: P) => (
  <svg {...base(size, 1.9)} {...p}>
    <rect x="3" y="5" width="18" height="14" rx="1.5" />
    <path d="M3 10h18" />
    <path d="M3 14.5h18" />
    <path d="M9 5v5" />
    <path d="M15 10v4.5" />
    <path d="M9 14.5V19" />
  </svg>
);
export const IconUpdates = ({ size = 20, ...p }: P) => (
  <svg {...base(size)} {...p}>
    <path d="M12 3v12" />
    <path d="m7 10 5 5 5-5" />
    <path d="M4 19h16" />
  </svg>
);
